<template>
  <div v-if="errorMessage === 'ok'">
    <div class="container">
      <h1 class="review-title">
        {{ $t('rescheduleForm.successTitle') }}
      </h1>
      <p v-if="formData.SLMAppLink">
        {{
          $t('rescheduleForm.appDeliveryMessage', {
            type: formData.isReturn
              ? $t('rescheduleForm.deliveryLower')
              : $t('rescheduleForm.pickupLower'),
          })
        }}
        <a href="https://sp.skroutzlastmile.gr/app" target="_blank">link</a>
      </p>
      <p>{{ $t('rescheduleForm.thankYou') }}</p>
    </div>
  </div>
  <div v-else-if="isOrderCompleted">
    <div class="container">
      <h1 class="review-title">
        {{ $t('rescheduleForm.orderCompleted') }}
      </h1>
      <p>{{ $t('rescheduleForm.thankYou') }}</p>
    </div>
  </div>
  <div v-else-if="errorMessage === 'Η παραγγελία δεν βρέθηκε'">
    <div class="container">
      <h1 class="review-title">
        {{ $t('rescheduleForm.orderNotFound') }}
      </h1>
      <p>{{ $t('rescheduleForm.thankYou') }}</p>
    </div>
  </div>
  <div v-else-if="errorMessage === 'Δεν δόθηκε σωστός κωδικός παραγγελίας'">
    <div class="container">
      <h1 class="review-title">
        {{ $t('rescheduleForm.wrongOrderCode') }}
      </h1>
      <p>{{ $t('rescheduleForm.thankYou') }}</p>
    </div>
  </div>
  <div v-else-if="errorMessage === 'Η παραγγελία δεν μπορεί να επαναδρομολογηθεί.'">
    <div class="container">
      <h1 class="review-title">
        {{ $t('rescheduleForm.cannotReschedule') }}
      </h1>
      <p>{{ $t('rescheduleForm.thankYou') }}</p>
    </div>
  </div>
  <div v-else-if="errorMessage !== 'ok'">
    <Toast
      v-if="showToast"
      :message="errorMessage"
      :success="errorMessage === 'ok'"
      @close="showToast = false"
      class="toast-override"
    />
    <div class="form-container">
      <h2>{{ $t('rescheduleForm.formTitle') }}</h2>

      <div>
        <label for="choiceToPickup"
          >{{
            formData.isReturn ? $t('rescheduleForm.delivery') : $t('rescheduleForm.pickup')
          }}:</label
        >
        <select v-model="formData.choiceToPickup" id="choiceToPickup" required>
          <option value="" disabled selected>{{
            $t('rescheduleForm.choiceDefaultOption', {
              type: formData.isReturn
                ? $t('rescheduleForm.deliveryGenitive')
                : $t('rescheduleForm.pickupGenitive'),
            })
          }}</option>
          <option v-if="formData.suggestLocker" value="locker">{{
            $t('rescheduleForm.lockerOption')
          }}</option>
          <option v-if="!formData.forceNewAddress" value="defaultAddress">{{
            $t('rescheduleForm.defaultAddressOption')
          }}</option>
          <option value="newAddress">{{ $t('rescheduleForm.newAddressOption') }}</option>
        </select>
      </div>

      <div v-if="formData.choiceToPickup === 'locker'">
        <label for="nearbyLockers" class="locker-label">
          {{ $t('rescheduleForm.selectLockerLabel') }}
          <div class="tooltip-container">
            <span class="info-icon">ⓘ</span>
            <div class="tooltip-text">
              {{
                $t('rescheduleForm.tooltipDistance', {
                  type: formData.isReturn
                    ? $t('rescheduleForm.deliveryGenitive')
                    : $t('rescheduleForm.pickupGenitive'),
                })
              }}
            </div>
          </div>
        </label>
        <select v-model="formData.selectedLocker" id="nearbyLockers" required>
          <option :value="null" disabled selected>{{ $t('rescheduleForm.selectOption') }}</option>
          <option
            v-for="locker in formData.nearbyLockers"
            :key="locker.locker.id"
            :value="locker.locker"
            >{{ locker.locker.friendlyName + ' ' + locker.distance + 'm' }}</option
          >
        </select>
      </div>
      <div v-else-if="formData.choiceToPickup === 'newAddress'">
        <label for="addresses">{{ $t('rescheduleForm.addressLabel') }}</label>
        <select
          v-model="formData.address"
          id="addresses"
          required
          @change="updateSelectedAddressId"
        >
          <option value="" disabled selected>{{
            $t('rescheduleForm.selectNewAddressDefault')
          }}</option>
          <option
            v-for="address in formData.addresses"
            :key="address.addressId"
            :value="address.addressName"
            >{{
              address.addressName !== '+ Νέα διεύθυνση'
                ? address.addressName
                : getNewAddressLocalizationIfNeeded()
            }}</option
          >
        </select>
      </div>

      <div v-else-if="formData.choiceToPickup === 'defaultAddress' && !formData.forceNewAddress">
        <label for="defaultAddress">{{ $t('rescheduleForm.addressLabel') }}</label>
        <input
          class="address-input"
          type="text"
          v-model="formData.address"
          id="defaultAddress"
          disabled
          required
          autocomplete="shipping street-address"
        />
      </div>

      <div
        v-if="formData.choiceToPickup === 'newAddress' && formData.address === '+ Νέα διεύθυνση'"
      >
        <label for="newAddress"
          >{{ $t('rescheduleForm.newAddressLabel') }}
          <span> <br />{{ $t('rescheduleForm.newAddressHint') }}</span></label
        >
        <AutoCompleteWithPlusCode
          ref="autoComplete"
          :default-value="formData.newAddress"
          :prefilled-address="mapPickedAddress"
          :current-coords="mapAddressCenter"
          :has-pin-placed="!!(formData.newAddressLat && formData.newAddressLng)"
          :map-open="showMapModal"
          :error="addressErrorHighlight"
          :placeholder="$t('autoComplete.searchAddress')"
          @input-change="handleAddressInputChange"
          @open-map-picker="handleOpenMapPicker"
        />

        <!-- Map Picker Modal -->
        <!-- v-show keeps the Map instance alive across close/reopen; v-if on the
        child ensures we don't mount it until the user actually opens the modal. -->
        <div
          v-show="showMapModal"
          class="map-modal-overlay"
          @mousedown.self="mapOverlayMouseDown = true"
          @click.self="handleMapOverlayClick"
          @touchmove.self.prevent
        >
          <div class="map-modal-content">
            <button type="button" class="map-modal-close" @click="dismissMapModal">×</button>
            <h3 class="map-modal-title">
              {{
                mapBoundary
                  ? $t('rescheduleForm.mapModalTitleWithBoundary')
                  : $t('rescheduleForm.mapModalTitleNoBoundary')
              }}
            </h3>
            <div v-if="!mapBoundary" class="map-place-pin-info">
              {{ $t('rescheduleForm.mapPlacePinInfo') }}
            </div>
            <AddressPickerMap
              v-if="mapEverOpened"
              ref="addressMap"
              :initial-center="mapModalCenter"
              :boundary-center="mapBoundary ? mapBoundary.center : null"
              :boundary-radius="mapBoundary ? mapBoundary.radius : 100"
              @address-picked="handleMapPicked"
              @out-of-bounds="handleMapOutOfBounds"
            />
            <div v-if="mapOutOfBounds" class="map-boundary-warning">
              {{ $t('rescheduleForm.mapBoundaryWarning') }}
            </div>
            <div v-else-if="mapOutOfCountry" class="map-boundary-warning">
              {{ $t('rescheduleForm.mapCountryWarning') }}
            </div>
            <div class="map-address-preview" :class="{ 'has-address': !!pendingMapPick }">
              <span v-if="manualFormattedAddress" class="map-address-preview-text">
                {{ manualFormattedAddress }}
              </span>
              <span v-else class="map-address-preview-placeholder">
                {{ $t('rescheduleForm.mapDragHint') }}
              </span>
              <span v-if="pendingMapPick" class="map-address-coords">
                📍 {{ pendingMapPick.lat.toFixed(6) }}, {{ pendingMapPick.lng.toFixed(6) }}
              </span>
            </div>
            <div class="map-modal-actions">
              <button
                v-if="mapSource === 'manual'"
                type="button"
                class="map-modal-back-btn"
                @click="handleMapBack"
              >
                {{ $t('rescheduleForm.mapBackButton') }}
              </button>
              <button type="button" class="map-modal-clear-btn" @click="requestClearPickedAddress">
                {{ $t('rescheduleForm.clearAddressButton') }}
              </button>
              <button
                type="button"
                class="map-modal-accept-btn"
                :disabled="!pendingMapPick || mapRequiresPinMove"
                @click="requestAcceptMapAddress"
              >
                {{ $t('rescheduleForm.mapAcceptButton') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Accept address confirmation -->
      <div v-if="showMapAcceptConfirm" class="map-confirm-overlay">
        <div class="map-confirm-content">
          <p>{{ $t('rescheduleForm.mapAcceptConfirmText') }}</p>
          <div class="map-confirm-buttons">
            <button type="button" class="confirm-btn" @click="confirmAcceptMapAddress">
              {{ $t('rescheduleForm.confirmSubmitButton') }}
            </button>
            <button type="button" class="cancel-btn" @click="cancelAcceptMapAddress">
              {{ $t('rescheduleForm.cancelButton') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Clear address confirmation -->
      <div v-if="showMapClearConfirm" class="map-confirm-overlay">
        <div class="map-confirm-content">
          <p>{{ $t('rescheduleForm.mapClearConfirmText') }}</p>
          <div class="map-confirm-buttons">
            <button type="button" class="map-modal-clear-btn" @click="confirmClearPickedAddress">
              {{ $t('rescheduleForm.clearAddressButton') }}
            </button>
            <button type="button" class="cancel-btn" @click="cancelClearPickedAddress">
              {{ $t('rescheduleForm.cancelButton') }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="formData.choiceToPickup !== '' && formData.choiceToPickup !== 'locker'">
        <label for="date">{{ $t('rescheduleForm.dateLabel') }}</label>

        <select v-model="formData.changedDate" id="date" required>
          <option value="" disabled selected>{{ $t('rescheduleForm.dateDefaultOption') }}</option>
          <option v-for="date in formData.availableDates" :value="date" :key="date">{{
            date
          }}</option>
        </select>
      </div>

      <div
        v-if="
          formData.choiceToPickup !== 'locker' &&
            formData.choiceToPickup !== '' &&
            formData.changedDate !== ''
        "
      >
        <label for="time">{{ $t('rescheduleForm.timeLabel') }}</label>
        <select v-model="formData.changedTime" id="time" required>
          <option value="" disabled selected>{{ $t('rescheduleForm.timeDefaultOption') }}</option>
          <option v-for="time in timeWindows" :value="time" :key="time">{{ time }}</option>
        </select>
      </div>

      <div v-if="formData.isInActiveRoute && formData.choiceToPickup !== ''">
        <label for="skipOnGoingDelivery">{{
          $t('rescheduleForm.activeRouteLabel', {
            type: formData.isReturn
              ? $t('rescheduleForm.pickupGenitive')
              : $t('rescheduleForm.deliveryGenitive'),
          })
        }}</label>
        <select v-model="formData.skipOnGoingDelivery" id="skipOnGoingDelivery" required>
          <option value="" disabled selected>{{ $t('rescheduleForm.selectOption') }}</option>
          <option value="yes">{{ $t('rescheduleForm.yes') }}</option>
          <option value="skip_ongoing_delivery">{{ $t('rescheduleForm.no') }}</option>
        </select>
      </div>

      <div v-if="formData.choiceToPickup !== 'locker' && formData.choiceToPickup !== ''">
        <label for="extraNotes"
          >{{ $t('rescheduleForm.notesLabel') }}
          <span
            ><br />
            {{ $t('rescheduleForm.notesHint') }}</span
          ></label
        >
        <textarea v-model="formData.extraNotes" id="extraNotes"></textarea>
      </div>

      <button @click="handleSubmit" class="submit-btn">
        {{ $t('rescheduleForm.submitButton') }}
      </button>
    </div>
    <div v-if="showConfirmation" class="confirmation-modal">
      <div class="confirmation-content">
        <h2>{{ $t('rescheduleForm.confirmModalTitle') }}</h2>
        <p>
          {{ $t('rescheduleForm.confirmModalPrefix') }}
          {{ formData.changedDate
          }}{{
            formData.changedTime
              ? $t('rescheduleForm.confirmModalTimePrefix') + formData.changedTime
              : ''
          }}
          {{
            formData.choiceToPickup === 'newAddress'
              ? $t('rescheduleForm.confirmModalAddressPrefix') +
                (formData.address === '+ Νέα διεύθυνση' ? formData.newAddress : formData.address)
              : ''
          }}{{
            formData.choiceToPickup === 'defaultAddress'
              ? $t('rescheduleForm.confirmModalAddressPrefix') + formData.address
              : ''
          }}{{
            formData.choiceToPickup === 'locker'
              ? $t('rescheduleForm.confirmModalLockerPrefix') + formData.selectedLocker.friendlyName
              : ''
          }};
        </p>
        <div class="confirmation-buttons">
          <button @click="confirmSubmit" class="confirm-btn" :disabled="isSubmitting">
            <span v-if="!isSubmitting">{{ $t('rescheduleForm.confirmSubmitButton') }}</span>
            <span v-else class="spinner-container">
              <div class="spinner"></div>
              {{ $t('rescheduleForm.processingText') }}
            </span>
          </button>
          <button @click="cancelSubmit" class="cancel-btn" :disabled="isSubmitting">
            {{ $t('rescheduleForm.cancelButton') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Toast from '../../graphic/Toast.vue';
import AutoCompleteWithPlusCode from './AutoCompleteWithPlusCode.vue';
import AddressPickerMap from './AddressPickerMap.vue';

export default {
  name: 'RescheduleForm',
  components: {
    Toast,
    AutoCompleteWithPlusCode,
    AddressPickerMap,
  },
  props: {
    errorMessage: {
      type: String,
      require: false,
    },
    data: {
      type: Object,
      require: true,
    },
    reschedule: {
      type: Function,
      require: true,
    },
  },
  data() {
    return {
      formData: this.data,
      weekdayTimeWindows: [
        '9:00-14:00',
        '9:00-17:00',
        '9:00-19:00',
        '12:00-16:00',
        '12:00-19:00',
        '14:00-19:00',
      ],
      weekendTimeWindows: ['9:00-14:00', '9:00-17:00', '12:00-16:00', '13:00-17:00'],
      showToast: !!this.errorMessage,
      showConfirmation: false,
      timeWindows: [],
      isSubmitting: false,
      mapPickedAddress: null,
      showMapModal: false,
      mapEverOpened: false,
      pendingMapPick: null,
      selectedAddressCoords: null,
      mapAddressCenter: null,
      mapOverlayMouseDown: false,
      mapBoundary: null,
      mapSource: null,
      manualFormattedAddress: null,
      manualCustomAddressData: null,
      showMapAcceptConfirm: false,
      showMapClearConfirm: false,
      mapOutOfBounds: false,
      mapOutOfCountry: false,
      lastRejectedCoords: null,
      addressConfirmed: false,
      addressErrorHighlight: false,
    };
  },
  watch: {
    errorMessage(newVal) {
      if (newVal) {
        this.showToast = true;
      }
    },
    'formData.choiceToPickup'(newVal) {
      if (newVal !== 'locker') {
        // reschedule() checks selectedLocker first, so a stale one silently overrides the
        // whole request - address and date included.
        this.formData.selectedLocker = null;
      }
      if (newVal === 'newAddress') {
        this.formData.address = '';
        this.formData.addressId = null; // re-set by updateSelectedAddressId on @change
      } else {
        if (newVal === 'defaultAddress') {
          this.formData.address = this.formData.defaultAddress;
          // Prefer the id the backend resolved for us. Falling back to the display-string
          // match keeps this working while /rescheduleDetails has not shipped
          // defaultAddressId yet, and for orders whose address FK was nulled.
          this.formData.addressId =
            this.formData.defaultAddressId || this.addressIdForName(this.formData.defaultAddress);
        } else {
          this.formData.addressId = null;
        }
        // Leaving the new-address flow: drop everything it produced. Without clearing
        // newAddress, reschedule()'s ternary keeps preferring the abandoned address over
        // the one shown in the confirmation modal.
        this.formData.newAddress = '';
        this.formData.newAddressObject = null;
        this.formData.newAddressLat = null;
        this.formData.newAddressLng = null;
        this.formData.pinPlacedByUser = false;
        this.mapPickedAddress = null;
        this.selectedAddressCoords = null;
        this.mapAddressCenter = null;
      }
      this.resetAddressConfirmation();
    },
    'formData.changedDate': {
      handler(newDate) {
        if (newDate) {
          const selectedDate = moment(newDate, 'DD/MM/YYYY').toDate();
          const day = selectedDate.getDay();
          this.timeWindows =
            day === 0 || day === 6 ? this.weekendTimeWindows : this.weekdayTimeWindows;
          if (this.formData.changedTime && !this.timeWindows.includes(this.formData.changedTime)) {
            this.formData.changedTime = '';
          }
        }
      },
    },
    showMapModal(val) {
      document.body.style.overflow = val ? 'hidden' : '';
    },
  },
  computed: {
    isOrderCompleted() {
      return this.errorMessage === 'isCompleted' || this.data?.isCompleted === true;
    },
    mapModalCenter() {
      return (
        this.selectedAddressCoords ?? this.mapAddressCenter ?? { lat: 37.986114, lng: 23.695274 }
      );
    },
    countryCode() {
      return process.env.VUE_APP_COUNTRY_CODE;
    },
    mapRequiresPinMove() {
      return !this.mapBoundary && !(this.pendingMapPick && this.pendingMapPick.userDragged);
    },
    hasConfirmedNewAddress() {
      return !!(
        this.addressConfirmed &&
        this.formData.newAddressLat &&
        this.formData.newAddressLng &&
        this.formData.newAddress
      );
    },
  },
  beforeDestroy() {
    document.body.style.overflow = '';
  },
  methods: {
    showError(key, params) {
      this.errorMessage = this.$t(key, params);
      this.showToast = true;
    },

    handleSubmit() {
      const {
        choiceToPickup,
        address,
        newAddress,
        selectedLocker,
        changedDate,
        changedTime,
        isInActiveRoute,
        skipOnGoingDelivery,
        isReturn,
      } = this.formData;
      const deliveryType = isReturn
        ? this.$t('rescheduleForm.deliveryGenitive')
        : this.$t('rescheduleForm.pickupGenitive');
      const pickupType = isReturn
        ? this.$t('rescheduleForm.pickupGenitive')
        : this.$t('rescheduleForm.deliveryGenitive');
      const notLocker = choiceToPickup !== 'locker';

      if (!choiceToPickup)
        return this.showError('rescheduleForm.errorNoChoice', { type: deliveryType });
      if (choiceToPickup === 'newAddress' && address === '')
        return this.showError('rescheduleForm.errorNoNewAddress');
      if (choiceToPickup === 'newAddress' && address === '+ Νέα διεύθυνση' && newAddress === '')
        return this.showError('rescheduleForm.errorNoNewAddressFilled');
      if (
        choiceToPickup === 'newAddress' &&
        address === '+ Νέα διεύθυνση' &&
        !this.hasConfirmedNewAddress
      ) {
        this.addressErrorHighlight = true;
        return this.showError('rescheduleForm.errorAddressNotConfirmed');
      }
      if (choiceToPickup === 'locker' && !selectedLocker)
        return this.showError('rescheduleForm.errorNoLocker');
      if (!changedDate && notLocker) return this.showError('rescheduleForm.errorNoDate');
      if (!changedTime && notLocker) return this.showError('rescheduleForm.errorNoTime');
      if (isInActiveRoute && !skipOnGoingDelivery)
        return this.showError('rescheduleForm.errorNoDeliveryAttempt', { type: pickupType });
      if (!address) return this.showError('rescheduleForm.errorNoAddress');

      this.showConfirmation = true;
    },
    async confirmSubmit() {
      this.isSubmitting = true;
      try {
        await this.reschedule(this.formData);
        this.showConfirmation = false;
      } catch {
        this.showError('rescheduleForm.errorGeneric');
      } finally {
        this.isSubmitting = false;
      }
    },

    cancelSubmit() {
      if (!this.isSubmitting) {
        this.showConfirmation = false;
      }
    },
    // addresses[] exposes only { addressName, addressId } - no validity flag - so the id has
    // to be resolved by display string. When the same addressName appears more than once, find()
    // returns the first, which the backend has already sorted to be the best row:
    // /rescheduleDetails runs sortCustomerAddresses (deliveryConfirmed, then isValid) before
    // mapping, and both of those flags imply a geocoded location. Do not reorder client-side.
    addressIdForName(addressName) {
      if (!addressName || addressName === '+ Νέα διεύθυνση') return null;
      const list = this.formData.addresses || [];
      const isReal = a => a.addressName !== '+ Νέα διεύθυνση' && a.addressId !== 'newAddress';

      let match = list.find(a => isReal(a) && a.addressName === addressName);
      if (!match) {
        // defaultAddress and addresses[] are built by different backend formatters, so the
        // same address can render two ways. Most often it is the GR zip - the entity renders
        // "166 75", the order's own field "16675" - and sometimes accents or casing. Retry
        // with exactly those differences removed.
        const target = this.normalizeAddressForMatch(addressName);
        match = list.find(a => isReal(a) && this.normalizeAddressForMatch(a.addressName) === target);
      }
      return match ? match.addressId : null;
    },

    // Mirrors the backend's own leniency: filterCityName strips accents, and the zip is spaced
    // on one side only. Street + number + city + zip all have to agree for this to match, so
    // two genuinely different addresses cannot collide.
    normalizeAddressForMatch(value) {
      return String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '')
        .toUpperCase();
    },

    updateSelectedAddressId() {
      if (this.formData.address !== '+ Νέα διεύθυνση') {
        this.formData.newAddress = '';
        this.formData.newAddressObject = null;
        this.formData.newAddressLat = null;
        this.formData.newAddressLng = null;
        this.formData.pinPlacedByUser = false;
        this.mapPickedAddress = null;
        this.selectedAddressCoords = null;
        this.mapAddressCenter = null;
      }
      this.resetAddressConfirmation();
      // Assign unconditionally: a no-match must clear the id, not keep the previous one.
      this.formData.addressId = this.addressIdForName(this.formData.address);
    },

    resetAddressConfirmation() {
      this.addressConfirmed = false;
      this.addressErrorHighlight = false;
    },

    handleAddressInputChange(value) {
      this.formData.newAddress = value;
      this.resetAddressConfirmation();
      if (value === '') {
        this.mapPickedAddress = null;
        this.formData.newAddressLat = null;
        this.formData.newAddressLng = null;
        this.formData.pinPlacedByUser = false;
        this.formData.newAddressObject = null;
        this.selectedAddressCoords = null;
        this.mapAddressCenter = null;
      }
    },

    getNewAddressLocalizationIfNeeded() {
      if (!this.$i18n.locale.includes('el')) {
        return '+ new Address';
      }

      return '+ Νέα διεύθυνση';
    },

    handleMapPicked(pick) {
      this.pendingMapPick = pick ?? null;
      this.mapOutOfBounds = false;
      this.mapOutOfCountry = false;
      this.lastRejectedCoords = null;
    },

    handleMapOutOfBounds() {
      this.mapOutOfBounds = true;
    },

    handleOpenMapPicker({ center, boundary, source, formattedAddress, customAddressData }) {
      if (center) this.mapAddressCenter = center;
      // Ignore spurious calls (e.g. auto-focus returning to input when modal closes)
      if (this.showMapModal) return;
      this.mapBoundary = boundary || null;
      this.mapSource = source || (boundary ? 'autocomplete' : 'manual');
      this.manualFormattedAddress = formattedAddress || null;
      this.manualCustomAddressData = customAddressData || null;
      this.pendingMapPick = null;
      this.mapOverlayMouseDown = false;
      this.mapOutOfBounds = false;
      this.mapOutOfCountry = false;
      this.lastRejectedCoords = null;

      // First open mounts AddressPickerMap (its mounted() creates the Map and
      // bills one Dynamic Maps Load). Subsequent opens reuse the same instance —
      // we just push the new center/boundary into it via applyConfig().
      const wasMounted = this.mapEverOpened;
      this.mapEverOpened = true;
      this.showMapModal = true;
      if (wasMounted) {
        this.$nextTick(() => {
          if (this.$refs.addressMap) this.$refs.addressMap.applyConfig();
        });
      }
    },

    handleMapBack() {
      this.closeMapModal();
      this.$nextTick(() => {
        if (this.$refs.autoComplete) {
          this.$refs.autoComplete.openCustomAddressModal();
        }
      });
    },

    async requestAcceptMapAddress() {
      if (!this.pendingMapPick || this.mapRequiresPinMove) return;

      // Skip the reverse-geocode when the pin is still at the forward-geocoded
      // position we already validated in `proceedToMap`. Only re-check if the
      // user actually dragged the pin away from that anchor.
      if (!this.mapBoundary && this.pendingMapPick.userDragged && this.$refs.addressMap) {
        const { lat, lng } = this.pendingMapPick;

        // Dirty check: a previous Accept already rejected this exact position,
        // so re-show the same warning instead of spending another reverse-geocode.
        // `handleMapPicked` clears `lastRejectedCoords` on the next drag.
        if (
          this.lastRejectedCoords &&
          this.lastRejectedCoords.lat === lat &&
          this.lastRejectedCoords.lng === lng
        ) {
          this.mapOutOfCountry = true;
          return;
        }

        const resolvedCountry = await this.$refs.addressMap.reverseGeocodeCountry(lat, lng);
        if (resolvedCountry && resolvedCountry !== this.countryCode) {
          this.lastRejectedCoords = { lat, lng };
          this.mapOutOfCountry = true;
          return;
        }
        this.lastRejectedCoords = null;
        this.mapOutOfCountry = false;
      }

      this.showMapAcceptConfirm = true;
    },

    confirmAcceptMapAddress() {
      this.showMapAcceptConfirm = false;
      this.acceptMapAddress();
    },

    cancelAcceptMapAddress() {
      this.showMapAcceptConfirm = false;
    },

    requestClearPickedAddress() {
      this.showMapClearConfirm = true;
    },

    confirmClearPickedAddress() {
      this.showMapClearConfirm = false;
      this.clearPickedAddress();
    },

    cancelClearPickedAddress() {
      this.showMapClearConfirm = false;
    },

    clearPickedAddress() {
      this.closeMapModal();
      if (this.$refs.autoComplete) {
        this.$refs.autoComplete.clearAddress();
      }
    },

    closeMapModal() {
      this.showMapModal = false;
    },

    handleMapOverlayClick() {
      if (this.mapOverlayMouseDown) {
        this.dismissMapModal();
      }
      this.mapOverlayMouseDown = false;
    },

    // Walking away from the map (× or overlay click) means no pin was accepted,
    // so the address goes with it — leaving the text in the input would read as
    // a confirmed address. `handleMapBack` and `acceptMapAddress` keep it.
    dismissMapModal() {
      this.pendingMapPick = null;
      this.manualFormattedAddress = null;
      this.manualCustomAddressData = null;
      this.clearPickedAddress();
    },

    acceptMapAddress() {
      if (!this.pendingMapPick) return;
      const { lat, lng, userDragged } = this.pendingMapPick;
      const isFirstPin = !this.formData.newAddressLat;

      this.formData.newAddressLat = lat;
      this.formData.newAddressLng = lng;
      this.selectedAddressCoords = { lat, lng };
      this.formData.pinPlacedByUser = this.formData.pinPlacedByUser || !!userDragged;

      const data = this.manualCustomAddressData;
      this.mapPickedAddress = data;
      this.formData.newAddressObject =
        data?.street && data?.streetNumber && data?.area && data?.postalCode ? data : null;

      // Always submit the resolved address (autocomplete description or the
      // formatted manual components), never the raw text left in the input.
      this.formData.newAddress = this.manualFormattedAddress || this.formData.newAddress;

      if (this.mapSource === 'manual') {
        // Anchor on the first pin so the boundary circle doesn't drift on re-open.
        if (isFirstPin) {
          this.mapAddressCenter = { lat, lng };
        }
      }

      this.addressConfirmed = true;
      this.addressErrorHighlight = false;
      this.closeMapModal();
    },
  },
};
</script>

<style scoped>
.tooltip-container {
  position: relative;
  display: inline-block;
  margin-left: 5px;
}

.info-icon {
  cursor: pointer;
  color: #4caf50;
  font-weight: bold;
}

.tooltip-text {
  visibility: hidden;
  width: 250px;
  background-color: #333;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 8px;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
  font-size: 14px;
  font-weight: normal;
}

.tooltip-text::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #333 transparent transparent transparent;
}

.tooltip-container:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

/* Container and Form Styles */
.address-input:disabled {
  background-color: #f0f0f0;
  color: #7a7a7a;
  border-color: #dcdcdc;
}

.form-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 90vw;
  max-width: 560px;
  margin: 20px auto;
  padding: 20px;
  background-color: #f9f9f9;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-size: 16px;
}

h2 {
  font-size: 1.2rem;
  margin-bottom: 20px;
  text-align: center;
  color: #333;
}

input,
select,
textarea {
  padding: 12px;
  margin-bottom: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
  font-size: 1rem;
}

input:focus,
select:focus,
textarea:focus {
  border-color: #4caf50;
  outline: none;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.2);
}

textarea {
  resize: vertical;
  min-height: 80px;
}

button {
  padding: 12px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  width: 100%;
  transition: background-color 0.3s;
}

button:hover {
  background-color: #45a049;
}

.confirmation-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.confirmation-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 400px;
  text-align: center;
}

.confirmation-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
}

.confirm-btn {
  background-color: #4caf50;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  width: auto;
}

.cancel-btn {
  background-color: #f44336;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  width: auto;
}

.confirm-btn:hover {
  background-color: #45a049;
}

.cancel-btn:hover {
  background-color: #d32f2f;
}
.spinner-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.confirm-btn:disabled,
.cancel-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.confirm-btn:disabled:hover {
  background-color: #4caf50 !important;
}

.cancel-btn:disabled:hover {
  background-color: #f44336 !important;
}

.map-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  padding: 12px;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  touch-action: none;
  z-index: 1000;
}

.map-modal-content {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.28);
  width: 92%;
  max-width: 820px;
  max-height: 90vh;
  max-height: 90dvh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
  padding: 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.map-modal-title,
.map-place-pin-info,
.map-boundary-warning,
.map-address-preview,
.map-modal-actions {
  flex-shrink: 0;
}

.map-modal-close {
  position: absolute;
  top: 10px;
  right: 12px;
  background: transparent;
  border: none;
  font-size: 22px;
  line-height: 1;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: auto;
}

.map-modal-close:hover {
  background: transparent;
  color: #333;
}

.map-modal-title {
  margin: 0;
  padding: 0 26px;
  font-size: 1rem;
  color: #333;
  text-align: center;
  line-height: 1.35;
  overflow-wrap: break-word;
}

.map-modal-actions {
  display: flex;
  gap: 10px;
}

.map-modal-back-btn,
.map-modal-clear-btn {
  background-color: transparent;
  color: #555;
  border: 1px solid #ccc;
  flex: 0 0 auto;
  width: auto;
  padding: 10px 20px;
}

.map-modal-back-btn:hover,
.map-modal-clear-btn:hover {
  background-color: #f0f0f0;
}

.map-modal-clear-btn {
  color: #c0392b;
  border-color: #e0c0bd;
}

.map-modal-clear-btn:hover {
  background-color: #fdf0ef;
}

.map-modal-accept-btn {
  flex: 1;
}

.map-modal-accept-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.map-modal-accept-btn:disabled:hover {
  background-color: #4caf50 !important;
}

.map-address-preview {
  min-height: 42px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 10px 14px;
  border-radius: 6px;
  border: 1px dashed #ccc;
  background-color: #f9f9f9;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.map-place-pin-info {
  padding: 10px 14px;
  border-radius: 6px;
  border: 1px solid #f0c36d;
  background-color: #fff8e1;
  color: #8d6708;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
}

.map-boundary-warning {
  padding: 10px 14px;
  border-radius: 6px;
  border: 1px solid #e53935;
  background-color: #fdecea;
  color: #c62828;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
}

.map-address-preview.has-address {
  border-color: #4caf50;
  background-color: #f1f8f1;
  border-style: solid;
}

.map-address-preview-text {
  font-size: 0.9rem;
  color: #2e7d32;
  font-weight: 500;
  word-break: break-word;
}

.map-address-preview-placeholder {
  font-size: 0.85rem;
  color: #aaa;
  font-style: italic;
}

.map-address-coords {
  display: block;
  font-size: 0.8rem;
  color: #4caf50;
  font-weight: 500;
  margin-top: 6px;
}

.toast-override {
  z-index: 99999 !important;
  position: fixed !important;
}

@media only screen and (max-width: 600px) {
  .form-container {
    padding: 15px;
    width: 95vw;
  }

  h2 {
    font-size: 1.1rem;
  }

  input,
  select,
  textarea {
    font-size: 0.9rem;
  }

  button {
    font-size: 0.9rem;
  }

  .confirmation-content {
    width: 85%;
    padding: 15px;
  }

  .confirmation-buttons {
    flex-direction: column;
    gap: 10px;
  }

  .confirm-btn,
  .cancel-btn {
    width: 100%;
  }

  .map-modal-content {
    width: 100%;
    padding: 14px;
    gap: 10px;
    border-radius: 8px;
  }

  .map-modal-title {
    padding: 0 24px;
    font-size: 0.95rem;
  }

  .map-modal-actions {
    flex-wrap: wrap;
  }

  .map-modal-accept-btn {
    flex: 1 1 100%;
    order: 0;
  }

  .map-modal-back-btn,
  .map-modal-clear-btn {
    flex: 1 1 auto;
    min-width: 0;
    padding: 10px 12px;
    order: 1;
    margin-top: 8px;
  }
}

.map-confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
}

.map-confirm-content {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.28);
  width: 90%;
  max-width: 420px;
  padding: 28px 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: center;
}

.map-confirm-content p {
  margin: 0;
  font-size: 1rem;
  color: #333;
  line-height: 1.5;
}

.map-confirm-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.map-confirm-buttons .confirm-btn,
.map-confirm-buttons .cancel-btn,
.map-confirm-buttons .map-modal-clear-btn {
  flex: 1;
}
</style>
